/**
 * Shared constants for the Atendimento (WhatsApp) module.
 * Used by both frontend (settings) and interno (config) views.
 */

export const STATUS_COLORS = {
  conectado: '#4CAF50',
  conectando: '#FFC107',
  desconectado: '#F44336',
  erro: '#F44336',
}

export const STATUS_LABELS = {
  conectado: 'Conectado',
  conectando: 'Conectando',
  desconectado: 'Desconectado',
  erro: 'Erro',
}

export const TIPO_LABELS = {
  compartilhado: 'Compartilhada',
  individual: 'Individual',
}

/**
 * Prazo (ms) das chamadas do fluxo de pareamento do WhatsApp.
 *
 * Precisa ser MENOR que o timeout do gateway (ALB idle 60s; CloudFront origin
 * response 30-60s) para que quem encerra a espera seja o front — que sabe
 * explicar o que houve e oferecer "tentar novamente" — e não o gateway com um
 * 504 opaco, que era o que deixava o modal girando indefinidamente.
 *
 * 45s cobre o caminho feliz da Evolution (guard ~10s + criação da instância
 * ~30s) sem alcançar o piso de 60s do gateway.
 */
export const CONEXAO_REQUEST_TIMEOUT_MS = 45000

/**
 * Mensagens do modal de QR Code. Ficam aqui — e não espalhadas pelo componente —
 * porque os testes asseguram o texto exibido ao usuário: PT-BR, sem jargão HTTP
 * e sempre com a próxima ação.
 */
export const QRCODE_MENSAGENS = {
  gerando: 'Gerando QR Code...',
  demora: 'Isso pode levar até 1 minuto.',
  timeout:
    'A conexão com o WhatsApp demorou mais que o esperado. Costuma ser instabilidade momentânea — tente novamente.',
  falha:
    'Não foi possível gerar o QR Code. Tente novamente ou contate o suporte.',
  tentarNovamente: 'Tentar novamente',
}

/**
 * Status que significam "demorou demais", não "deu errado".
 *
 * 408 é o deadline do PRÓPRIO cliente (ver `CONEXAO_REQUEST_TIMEOUT_MS`); os
 * 5xx de gateway são a mesma demora vista do outro lado. Os dois casos pedem a
 * mesma orientação — esperar e tentar de novo — e nenhum deles tem mensagem
 * própria: o que chega no envelope é a string do axios, em inglês.
 */
export const QRCODE_STATUS_DEMORA = [408, 502, 503, 504]

/**
 * Status cujo texto vem do BODY do backend (contrato HTTP 200 always) e já
 * chega em PT-BR pronto para exibir — ex.: "Conexão não encontrada", "Sem
 * permissão para esta conexão". Fora desta lista, a mensagem é de transporte e
 * NUNCA deve ser mostrada ao usuário.
 */
export const QRCODE_STATUS_MENSAGEM_DO_BACKEND = [400, 403, 404, 409]

/**
 * Vereditos de NEGÓCIO no polling — valem na primeira vez, sem tolerância.
 *
 * O contador de 3 erros existe para falha transitória de transporte (timeout,
 * 5xx): insistir faz sentido. Estes não: "sem permissão", "conexão não
 * encontrada" e "instância já aberta" não mudam por tentar de novo, e esperar
 * três ticks só atrasa a orientação em 15s ou mais.
 *
 * Ficou explícito quando o `POST /connect` virou assíncrono (spec-002): o 409 do
 * guard de instância aberta, que antes voltava na hora pela própria rota, passou
 * a chegar pelo polling.
 */
export const QRCODE_STATUS_VEREDITO_DEFINITIVO = [403, 404, 409]

/**
 * Vestígios de detalhe técnico numa mensagem — se algum aparecer, o texto NÃO
 * chega ao usuário.
 *
 * O backend nem sempre manda mensagem de produto no 400: `usecase_conexao.conectar()`
 * faz `f"Erro ao obter QR code: {e}"`, interpolando a exceção crua da Evolution.
 * Medido com a stack local no ar, o modal exibia
 * "Erro ao obter QR code: HTTPConnectionPool(host='172.24.0.1', port=8085):
 * Max retries exceeded..." — o mesmo problema do jargão do axios, entrando pela
 * porta do envelope de negócio.
 */
export const QRCODE_MENSAGEM_TECNICA =
  /HTTPConnectionPool|Max retries|Errno|Traceback|Exception|urllib3|ConnectionError|NewConnection|port=\d+|<[a-z_]+\.[a-z_]+/i

/**
 * Teto de espera do polling, em ms, contado do início do fluxo.
 *
 * Com o `POST /connect` assíncrono (spec-002) o backend responde na hora e o QR
 * passa a vir pelo polling — que devolve 200 "ainda não" indefinidamente
 * enquanto a Evolution não entrega. Sem um teto, isso reintroduz exatamente o
 * spinner infinito que a spec-001 removeu, só que por outro caminho: o front
 * ficaria girando porque cada resposta, isolada, é um sucesso.
 *
 * 90s cobre com folga o pior caso do encadeamento na Evolution (~130s só ocorre
 * quando cada etapa estoura o próprio timeout, cenário em que o QR não viria de
 * qualquer forma) sem deixar o usuário sem resposta por minutos.
 */
export const QRCODE_ESPERA_MAX_MS = 90000

/** Ticks de polling com erro antes de desistir e pedir ação ao usuário. */
export const QRCODE_MAX_ERROS_POLLING = 3
